import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import camelcaseKeys from 'camelcase-keys';

import { policyApi } from '../api';
import { Statuses } from '../constants/statuses';
import { createErrorToast, createSuccessToast, createToast } from '../redux/modules/app';
import { removeLoadingPolicyTypeId, setLoadingPolicyTypeId } from '../redux/modules/insuredProfilePage';
import { modifyArrayItemAt, removeArrayItemAt } from '../utils/arrayUtil';
import { getCurrentTimestamp, getDateString, isPast } from '../utils/dateUtil';
import { queryClient } from '../utils/queryClientUtil';

import insuredKeys from './models/insured/keys';
import policyKeys from './models/policy/keys';
import recordKeys from './models/record/keys';
import {
  camelCaseApiResponse,
  getPageData, optimisticUpdateActiveRecords,
  setOverallStatusAndRecordStatusAsFetching,
  updatePolicyRequirementsWithLoadingStatus
} from './util';

/* QUERIES
========================================================= */
export const usePolicyLinkedItemsData = ({
  policyId,
  insuredId,
  pageSize = 10,
  filter = {},
  include = [],
  options = {}
}) => useInfiniteQuery(
  policyKeys.policyLinkedItems(policyId),
  ({ pageParam = 1 }) => camelCaseApiResponse(() => policyApi.getItemsForPolicy(
    policyId,
    insuredId,
    include,
    filter,
    { page: pageParam, pageSize }
  )),
  {
    ...options,
    getNextPageParam: getPageData
  }
);

export const usePolicyTypeId = (policyTypeId, options = {}) => useQuery(
  policyKeys.policyType(policyTypeId),
  () => camelCaseApiResponse(() => policyApi.getPolicyType(policyTypeId)),
  options
);

export const useGetPolicyAgent = (policyId, options = {}) => useQuery(
  policyKeys.policyAgent(policyId),
  () => camelCaseApiResponse(() => policyApi.getPolicyAgent(policyId)),
  options
);

/* MUTATIONS
========================================================= */
export const useUpdatePolicy = ({ policyId, policyTypeId, recordId, insuredId, options = {} }) => {
  const dispatch = useDispatch();
  const activeDocumentsKey = recordKeys.activeRecordDocuments(recordId);
  const activePoliciesKey = recordKeys.activeRecordPolicies(recordId);
  const policyRequirements = recordKeys.recordPolicyRequirements(recordId);
  const itemRequirements = recordKeys.recordItemRequirements(recordId);
  const documentRequirements = recordKeys.recordDocumentRequirements(recordId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  return useMutation(
    policyData => {
      // set effective_at and expires_at to just date strings to ensure no time conversion occurs on submit
      policyData.details.effective_at = getDateString(policyData.details.effective_at, 'yyyy-mm-dd');
      policyData.details.expires_at = getDateString(policyData.details.expires_at, 'yyyy-mm-dd');

      return camelCaseApiResponse(() => policyApi.updatePolicy(policyId, policyData));
    },
    {
      onMutate: async updateData => {
        await queryClient.cancelQueries(activePoliciesKey);
        await queryClient.cancelQueries(policyRequirements);
        await queryClient.cancelQueries(documentRequirements);
        await queryClient.cancelQueries(overallStatus);
        await queryClient.cancelQueries(activeRecordStatuses);

        const previousPolicies = queryClient.getQueryData(activePoliciesKey);
        const previousPolicyRequirements = queryClient.getQueryData(policyRequirements);
        const previousItemRequirements = queryClient.getQueryData(itemRequirements);
        const previousDocumentRequirements = queryClient.getQueryData(documentRequirements);
        const previousOverallStatus = queryClient.getQueryData(overallStatus);
        const previousRecordStatuses = queryClient.getQueryData(activeRecordStatuses);

        // TODO: work on optimistic update
        // const modFunction = (policy, newPolicyData) => {
        //   const { details, properties } = camelcaseKeys(newPolicyData);
        //   const {
        //     number,
        //     subrogationWaived,
        //     additionalInsured,
        //     effectiveAt,
        //     expiresAt,
        //     insuranceCompanyId,
        //     insuranceCompanyDisplayName
        //   } = details;
        //
        //   const modProperties = policy.propertyValues.map(pv => {
        //     const hasUpdatedProperty = properties.find(p => pv.id === p.id);
        //
        //     if (hasUpdatedProperty) {
        //       return { ...pv, value: hasUpdatedProperty.value };
        //     }
        //
        //     return pv;
        //   });
        //
        //   const modDetails = {
        //     number,
        //     additionalInsured,
        //     subrogationWaived,
        //     effectiveAt,
        //     expiresAt,
        //     insuranceCompanyId,
        //     insuranceCompany: { ...policy.insuranceCompany, companyName: insuranceCompanyDisplayName },
        //     propertyValues: modProperties
        //   };
        //
        //   const value = { ...policy, ...modDetails };
        //
        //   console.log('CHECK MY WORK', { policy, modDetails, value });
        //
        //   return value;
        // };
        //
        // const newData = modifyArrayItemAt(
        //   previousPolicies,
        //   'id',
        //   policyId,
        //   policy => modFunction(policy, updateData)
        // );
        //
        // queryClient.setQueryData(policies, newData);

        updatePolicyRequirementsWithLoadingStatus({
          policiesKey: activePoliciesKey,
          policyRequirementsKey: policyRequirements,
          itemRequirementsKey: itemRequirements,
          previousPolicies,
          previousPolicyRequirements,
          previousItemRequirements,
          status: Statuses.Updating,
          policyId,
          policyTypeId
        });

        setOverallStatusAndRecordStatusAsFetching(insuredId, recordId);

        dispatch(createToast({ title: 'Updating Policy', duration: 60000 }));

        return { previousPolicies, previousPolicyRequirements, previousDocumentRequirements, previousOverallStatus, previousRecordStatuses };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your policy was not reactivated successfully.'
        }));

        queryClient.setQueryData(activePoliciesKey, context.previousPolicies);
        queryClient.setQueryData(policyRequirements, context.previousPolicyRequirements);
        queryClient.setQueryData(documentRequirements, context.previousDocumentRequirements);
        queryClient.setQueryData(overallStatus, context.previousOverallStatus);
        queryClient.setQueryData(activeRecordStatuses, context.previousRecordStatuses);
      },
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries(activePoliciesKey),
          queryClient.invalidateQueries(activeDocumentsKey),
          queryClient.invalidateQueries(policyRequirements),
          queryClient.invalidateQueries(documentRequirements)
        ]);

        dispatch(createSuccessToast({ title: 'Updated Policy' }));

        Promise.all([
          queryClient.invalidateQueries(overallStatus),
          queryClient.invalidateQueries(activeRecordStatuses)
        ]);
      }
    }
  );
};

export const useReactivatePolicy = ({ policyId, insuredId, jobId, recordId, policyTypeId = null }) => {
  const dispatch = useDispatch();
  const activePoliciesKey = recordKeys.activeRecordPolicies(recordId);
  const archivedPoliciesKey = recordKeys.archivedRecordPolicies(recordId);
  const policyRequirements = recordKeys.recordPolicyRequirements(recordId);
  const documentRequirements = recordKeys.recordDocumentRequirements(recordId);
  const itemRequirements = recordKeys.recordItemRequirements(recordId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  return useMutation(
    () => camelCaseApiResponse(() => policyApi.reactivatePolicy({ policyId, recordId })),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(activePoliciesKey);
        await queryClient.cancelQueries(archivedPoliciesKey);
        await queryClient.cancelQueries(policyRequirements);
        await queryClient.cancelQueries(overallStatus);
        await queryClient.cancelQueries(activeRecordStatuses);

        const previousActivePolicies = queryClient.getQueryData(activePoliciesKey);
        const previousArchivedPolicies = queryClient.getQueryData(archivedPoliciesKey);
        const previousOverallStatus = queryClient.getQueryData(overallStatus);
        const previousRecordStatuses = queryClient.getQueryData(activeRecordStatuses);
        const previousPolicyRequirements = queryClient.getQueryData(policyRequirements) ?? [];
        const previousItemRequirements = queryClient.getQueryData(itemRequirements) ?? [];

        // set selected policy details
        const selectedPolicy = previousArchivedPolicies.find(pol => pol.id === policyId);
        const selectedPolicyTypeId = selectedPolicy.insurancePolicyTypeId;
        const selectPolicyIsExpired = isPast(selectedPolicy.expiresAt);
        const currentPolicyTypeReq = [...previousPolicyRequirements, ...previousItemRequirements].find(polReq => polReq.insurancePolicyTypeId === selectedPolicyTypeId);
        const currentPolicyTypeStatus = currentPolicyTypeReq?.status;

        // optimistic update on active and archived policies
        const newActiveData = [
          ...previousActivePolicies,
          { ...selectedPolicy, archivedAt: null, status: Statuses.Active }
        ];

        const newArchivedData = removeArrayItemAt(
          previousArchivedPolicies,
          'id',
          policyId
        );

        queryClient.setQueryData(activePoliciesKey, newActiveData);
        queryClient.setQueryData(archivedPoliciesKey, newArchivedData);

        // calculate new policy requirement statuses
        // if policy req exists and the status isn't already waived, then optimistic updates are available
        if (currentPolicyTypeReq && currentPolicyTypeStatus !== Statuses.Waived) {
          let calcStatus = [Statuses.Compliant, Statuses.NonCompliant, Statuses.NotReceived].includes(currentPolicyTypeStatus) ? Statuses.Pending : currentPolicyTypeStatus;

          if (selectPolicyIsExpired) {
            calcStatus = Statuses.Expired;
          }

          // optimistic update policy reqs
          const newPolReqs = previousPolicyRequirements.map(polReq => {
            if (polReq.insurancePolicyTypeId === selectedPolicyTypeId) {
              return { ...polReq, status: calcStatus };
            }

            return polReq;
          });

          queryClient.setQueryData(policyRequirements, newPolReqs);

          const newItemReqs = previousItemRequirements.map(itemReq => {
            if (itemReq.insurancePolicyTypeId === selectedPolicyTypeId) {
              return { ...itemReq, status: calcStatus };
            }

            return itemReq;
          });

          queryClient.setQueryData(itemRequirements, newItemReqs);

          // optimistic update on active records
          optimisticUpdateActiveRecords({
            recordId,
            activeRecordsKey: activeRecordStatuses,
            polReqKey: policyRequirements,
            docReqKey: documentRequirements,
            itemReqKey: itemRequirements
          });
        }

        dispatch(createSuccessToast({ title: 'Reactivated Policy' }));

        return { previousActivePolicies, previousArchivedPolicies, previousPolicyRequirements, previousItemRequirements, previousOverallStatus, previousRecordStatuses };
      },
      onError: (_error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your policy was not reactivated successfully.'
        }));

        queryClient.setQueryData(activePoliciesKey, context.previousActivePolicies);
        queryClient.setQueryData(archivedPoliciesKey, context.previousArchivedPolicies);
        queryClient.setQueryData(policyRequirements, context.previousPolicyRequirements);
        queryClient.setQueryData(itemRequirements, context.previousItemRequirements);
        queryClient.setQueryData(overallStatus, context.previousOverallStatus);
        queryClient.setQueryData(activeRecordStatuses, context.previousRecordStatuses);
      },
      onSuccess: async () => {}
    }
  );
};

export const useArchivePolicy = ({ policyId, insuredId, jobId, recordId, policyTypeId = null }) => {
  const dispatch = useDispatch();
  const activePoliciesKey = recordKeys.activeRecordPolicies(recordId);
  const activeDocumentsKey = recordKeys.activeRecordDocuments(recordId);
  const documentRequirementsKey = recordKeys.recordDocumentRequirements(recordId);
  const archivedPoliciesKey = recordKeys.archivedRecordPolicies(recordId);
  const policyRequirements = recordKeys.recordPolicyRequirements(recordId);
  const itemRequirements = recordKeys.recordItemRequirements(recordId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  let invalidate = false;

  return useMutation(
    () => camelCaseApiResponse(() => policyApi.archivePolicy({ policyId, recordId })),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(activePoliciesKey);
        await queryClient.cancelQueries(activeDocumentsKey);
        await queryClient.cancelQueries(documentRequirementsKey);
        await queryClient.cancelQueries(archivedPoliciesKey);
        await queryClient.cancelQueries(policyRequirements);
        await queryClient.cancelQueries(overallStatus);
        await queryClient.cancelQueries(activeRecordStatuses);

        const previousActivePolicies = queryClient.getQueryData(activePoliciesKey);
        const previousActiveDocuments = queryClient.getQueryData(activeDocumentsKey);
        const previousDocumentRequirements = queryClient.getQueryData(documentRequirementsKey);
        const previousArchivedPolicies = queryClient.getQueryData(archivedPoliciesKey);
        const previousPolicyRequirements = queryClient.getQueryData(policyRequirements) ?? [];
        const previousItemRequirements = queryClient.getQueryData(itemRequirements) ?? [];
        const previousOverallStatus = queryClient.getQueryData(overallStatus);
        const previousRecordStatuses = queryClient.getQueryData(activeRecordStatuses);

        // get selected policy details
        const selectedPolicy = [...previousActivePolicies, ...previousArchivedPolicies].find(pol => pol.id === policyId);
        const selectedPolicyTypeId = selectedPolicy.insurancePolicyTypeId;
        const selectPolicyIsExpired = isPast(selectedPolicy.expiresAt);
        const currentPolicyTypeReq = [...previousPolicyRequirements, ...previousItemRequirements].find(polReq => polReq.insurancePolicyTypeId === selectedPolicyTypeId);
        const currentPolicyTypeStatus = currentPolicyTypeReq?.status;
        const currentRequirementPolicyStatus = currentPolicyTypeReq?.requirementPolicyStatus;

        dispatch(createSuccessToast({ title: 'Policy Archived' }));

        // Optimistic update active and archived policies lists
        const timestamp = getCurrentTimestamp();

        const newActivePolicies = removeArrayItemAt(
          previousActivePolicies,
          'id',
          policyId
        );

        const newArchivedPolicies = [
          ...previousArchivedPolicies,
          { ...selectedPolicy, archivedAt: timestamp, status: Statuses.Pending }
        ];

        queryClient.setQueryData(activePoliciesKey, newActivePolicies);
        queryClient.setQueryData(archivedPoliciesKey, newArchivedPolicies);

        // Calculate the new policy requirement status after policy is archived
        // if selected policy type is required and the status is not waived, then optimistic updates available
        if (currentPolicyTypeStatus && currentPolicyTypeStatus !== Statuses.Waived) {
          //  by default, the status should stay the same unless Compliant
          let calcStatus = currentPolicyTypeStatus === Statuses.Compliant ? Statuses.Pending : currentPolicyTypeStatus;

          const activePoliciesOfType = newActivePolicies.filter(actPol => actPol.insurancePolicyTypeId === selectedPolicyTypeId);

          // if no active policies of type exist, then new status is not received
          if (!activePoliciesOfType.length) {
            calcStatus = Statuses.NotReceived;
          } else {
            const expiredActivePolicyOfTypeExists = activePoliciesOfType.find(actPol => isPast(actPol.expiresAt));

            // if any are active but expired, the status is expired
            if (expiredActivePolicyOfTypeExists) {
              calcStatus = Statuses.Expired;
            } else if (currentPolicyTypeStatus === Statuses.Expired) {
              // else if the status was expired, set it back to the status before it expired
              calcStatus = currentRequirementPolicyStatus === Statuses.Compliant ? Statuses.Pending : currentRequirementPolicyStatus;
            }
          }

          // execute optimistic update for policy requirements
          const newPolReqs = previousPolicyRequirements.map(polReq => {
            if (polReq.insurancePolicyTypeId === selectedPolicyTypeId) {
              return { ...polReq, status: calcStatus };
            }

            return polReq;
          });

          queryClient.setQueryData(policyRequirements, newPolReqs);

          const newItemReqs = previousItemRequirements.map(itemReq => {
            if (itemReq.insurancePolicyTypeId === selectedPolicyTypeId) {
              return { ...itemReq, status: calcStatus };
            }

            return itemReq;
          });

          queryClient.setQueryData(itemRequirements, newItemReqs);
        }

        // finally, remove the policy from any linked documents.  If it is removed, invalidate doc requirements
        // remove policy from any doc links in activeDocumentsKey
        const newActiveDocuments = previousActiveDocuments.map(doc => {
          const docPolLength = doc.policies.length;

          const newPolicyLinks = removeArrayItemAt(
            doc.policies,
            'id',
            policyId
          );

          // set to invalidate if the policy was expired and was linked to any docs
          // UI updates, but we need doc reqs to invalidate for record/insured statuses
          if (!invalidate) {
            invalidate = selectPolicyIsExpired && docPolLength !== newPolicyLinks.length;
          }

          return { ...doc, policies: newPolicyLinks };
        });

        queryClient.setQueryData(activeDocumentsKey, newActiveDocuments);

        if (!invalidate) {
          // If we don't need to invalidate, we can optimistic update the active record status
          optimisticUpdateActiveRecords({
            recordId,
            activeRecordsKey: activeRecordStatuses,
            polReqKey: policyRequirements,
            docReqKey: documentRequirementsKey,
            itemReqKey: itemRequirements
          });
        } else {
          // set loading state for overall and record statuses
          setOverallStatusAndRecordStatusAsFetching(insuredId, recordId);
        }

        return {
          previousPolicies: previousActivePolicies,
          previousActiveDocuments,
          previousDocumentRequirements,
          previousArchivedPolicies,
          previousPolicyRequirements,
          previousItemRequirements,
          previousOverallStatus,
          previousRecordStatuses
        };
      },
      onError: (error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your policy was not archived.',
          error
        }));

        queryClient.setQueryData(activePoliciesKey, context.previousPolicies);
        queryClient.setQueryData(activeDocumentsKey, context.previousActiveDocuments);
        queryClient.setQueryData(documentRequirementsKey, context.previousDocumentRequirements);
        queryClient.setQueryData(archivedPoliciesKey, context.previousArchivedPolicies);
        queryClient.setQueryData(policyRequirements, context.previousPolicyRequirements);
        queryClient.setQueryData(itemRequirements, context.previousItemRequirements);
        queryClient.setQueryData(overallStatus, context.previousOverallStatus);
        queryClient.setQueryData(activeRecordStatuses, context.previousRecordStatuses);
      },
      onSuccess: async () => {
        if (invalidate) {
          await Promise.all([
            queryClient.invalidateQueries(activePoliciesKey),
            queryClient.invalidateQueries(archivedPoliciesKey),
            queryClient.invalidateQueries(policyRequirements),
            queryClient.invalidateQueries(activeRecordStatuses),
            queryClient.invalidateQueries(overallStatus)
          ]);

          Promise.all([
            queryClient.invalidateQueries(activeDocumentsKey),
            queryClient.invalidateQueries(documentRequirementsKey)
          ]);
        }
      }
    }
  );
};

export const useDeletePolicy = ({ policyId, insuredId, jobId, recordId, policyTypeId = null }) => {
  const dispatch = useDispatch();
  const activePoliciesKey = recordKeys.activeRecordPolicies(recordId);
  const activeDocumentsKey = recordKeys.activeRecordDocuments(recordId);
  const documentRequirementsKey = recordKeys.recordDocumentRequirements(recordId);
  const archivedPoliciesKey = recordKeys.archivedRecordPolicies(recordId);
  const policyRequirements = recordKeys.recordPolicyRequirements(recordId);
  const itemRequirements = recordKeys.recordItemRequirements(recordId);
  const overallStatus = insuredKeys.insuredOverallStatus(insuredId);
  const activeRecordStatuses = insuredKeys.insuredActiveRecords(insuredId);

  let invalidate = false;

  return useMutation(
    () => camelCaseApiResponse(() => policyApi.deletePolicy({ policyId, recordId })),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(activePoliciesKey);
        await queryClient.cancelQueries(activeDocumentsKey);
        await queryClient.cancelQueries(documentRequirementsKey);
        await queryClient.cancelQueries(archivedPoliciesKey);
        await queryClient.cancelQueries(policyRequirements);
        await queryClient.cancelQueries(overallStatus);
        await queryClient.cancelQueries(activeRecordStatuses);

        const previousActivePolicies = queryClient.getQueryData(activePoliciesKey);
        const previousActiveDocuments = queryClient.getQueryData(activeDocumentsKey);
        const previousDocumentRequirements = queryClient.getQueryData(documentRequirementsKey);
        const previousArchivedPolicies = queryClient.getQueryData(archivedPoliciesKey);
        const previousPolicyRequirements = queryClient.getQueryData(policyRequirements) ?? [];
        const previousItemRequirements = queryClient.getQueryData(itemRequirements) ?? [];
        const previousOverallStatus = queryClient.getQueryData(overallStatus);
        const previousRecordStatuses = queryClient.getQueryData(activeRecordStatuses);

        // get selected policy details
        const selectedPolicy = [...previousActivePolicies, ...previousArchivedPolicies].find(pol => pol.id === policyId);
        const selectedPolicyTypeId = selectedPolicy.insurancePolicyTypeId;
        const selectPolicyIsExpired = isPast(selectedPolicy.expiresAt);
        const selectedPolicyIsActive = !selectedPolicy.archivedAt && !selectedPolicy.deletedAt;
        const currentPolicyTypeReq = [...previousPolicyRequirements, ...previousItemRequirements].find(polReq => polReq.insurancePolicyTypeId === selectedPolicyTypeId);
        const currentPolicyTypeStatus = currentPolicyTypeReq?.status;
        const currentRequirementPolicyStatus = currentPolicyTypeReq?.requirementPolicyStatus;

        if (!selectedPolicy) {
          throw new Error(`Could not find the policy with id ${policyId} in active or archived policy caches`);
        }

        // optimistic update on active and archived policies
        const newActivePolicies = removeArrayItemAt(
          previousActivePolicies,
          'id',
          policyId
        );

        const newArchivedData = removeArrayItemAt(
          previousArchivedPolicies,
          'id',
          policyId
        );

        queryClient.setQueryData(activePoliciesKey, newActivePolicies);
        queryClient.setQueryData(archivedPoliciesKey, newArchivedData);

        // Calculate the new policy requirement status after policy is archived
        // if the selected policy is active, selected policy type is required, and the status is not waived, then optimistic updates available
        if (selectedPolicyIsActive && currentPolicyTypeStatus && currentPolicyTypeStatus !== Statuses.Waived) {
          //  by default, the status should stay the same unless Compliant
          let calcStatus = currentPolicyTypeStatus === Statuses.Compliant ? Statuses.Pending : currentPolicyTypeStatus;

          const activePoliciesOfType = newActivePolicies.filter(actPol => actPol.insurancePolicyTypeId === selectedPolicyTypeId);

          // if no active policies of type exist, then new status is not received
          if (!activePoliciesOfType.length) {
            calcStatus = Statuses.NotReceived;
          } else {
            const expiredActivePolicyOfTypeExists = activePoliciesOfType.find(actPol => isPast(actPol.expiresAt));

            // if any are active but expired, the status is expired
            if (expiredActivePolicyOfTypeExists) {
              calcStatus = Statuses.Expired;
            } else if (currentPolicyTypeStatus === Statuses.Expired) {
              // else if the status was expired, set it back to the status before it expired
              calcStatus = currentRequirementPolicyStatus === Statuses.Compliant ? Statuses.Pending : currentRequirementPolicyStatus;
            }
          }

          // execute optimistic update for policy requirements
          const newPolReqs = previousPolicyRequirements.map(polReq => {
            if (polReq.insurancePolicyTypeId === selectedPolicyTypeId) {
              return { ...polReq, status: calcStatus };
            }

            return polReq;
          });

          queryClient.setQueryData(policyRequirements, newPolReqs);

          const newItemReqs = previousItemRequirements.map(itemReq => {
            if (itemReq.insurancePolicyTypeId === selectedPolicyTypeId) {
              return { ...itemReq, status: calcStatus };
            }

            return itemReq;
          });

          queryClient.setQueryData(itemRequirements, newItemReqs);
        }

        // finally, if the policy is active, remove the policy from any linked documents.  If it is removed, invalidate doc requirements
        if (selectedPolicyIsActive) {
          // remove policy from any doc links in activeDocumentsKey
          const newActiveDocuments = previousActiveDocuments.map(doc => {
            const docPolLength = doc.policies.length;

            const newPolicyLinks = removeArrayItemAt(
              doc.policies,
              'id',
              policyId
            );

            // set to invalidate if the policy was expired and was linked to any docs
            // UI updates, but we need doc reqs to invalidate for record/insured statuses
            if (!invalidate) {
              invalidate = selectPolicyIsExpired && docPolLength !== newPolicyLinks.length;
            }

            return { ...doc, policies: newPolicyLinks };
          });

          queryClient.setQueryData(activeDocumentsKey, newActiveDocuments);
        }

        if (!invalidate) {
          // If we don't need to invalidate, we can optimistic update the active record status
          optimisticUpdateActiveRecords({
            recordId,
            activeRecordsKey: activeRecordStatuses,
            polReqKey: policyRequirements,
            docReqKey: documentRequirementsKey,
            itemReqKey: itemRequirements
          });
        } else {
          // set loading state for overall and record statuses
          setOverallStatusAndRecordStatusAsFetching(insuredId, recordId);
        }

        dispatch(createSuccessToast({ title: 'Policy Deleted' }));

        return {
          previousPolicies: previousActivePolicies,
          previousActiveDocuments,
          previousDocumentRequirements,
          previousArchivedPolicies,
          previousPolicyRequirements,
          previousItemRequirements,
          previousOverallStatus,
          previousRecordStatuses,
          selectedPolicyTypeId: selectedPolicy.insurancePolicyTypeId
        };
      },
      onError: (error, _insured, context) => {
        dispatch(createErrorToast({
          title: 'Oops! Something went wrong...',
          description: 'Your policy was not deleted.',
          error
        }));

        queryClient.setQueryData(activePoliciesKey, context.previousPolicies);
        queryClient.setQueryData(activeDocumentsKey, context.previousActiveDocuments);
        queryClient.setQueryData(documentRequirementsKey, context.previousDocumentRequirements);
        queryClient.setQueryData(archivedPoliciesKey, context.previousArchivedPolicies);
        queryClient.setQueryData(policyRequirements, context.previousPolicyRequirements);
        queryClient.setQueryData(itemRequirements, context.previousItemRequirements);
        queryClient.setQueryData(overallStatus, context.previousOverallStatus);
        queryClient.setQueryData(activeRecordStatuses, context.previousRecordStatuses);
      },
      onSuccess: async (_data, _variables, context) => {
        if (invalidate) {
          await Promise.all([
            queryClient.invalidateQueries(activePoliciesKey),
            queryClient.invalidateQueries(archivedPoliciesKey),
            queryClient.invalidateQueries(policyRequirements),
            queryClient.invalidateQueries(overallStatus),
            queryClient.invalidateQueries(activeRecordStatuses)
          ]);

          dispatch(removeLoadingPolicyTypeId(context.selectedPolicyTypeId));

          Promise.all([
            queryClient.invalidateQueries(activeDocumentsKey),
            queryClient.invalidateQueries(documentRequirementsKey)
          ]);
        }
      }
    }
  );
};
